/* eslint-disable @typescript-eslint/no-unused-vars */
import { call, put, select, delay, takeLatest, take } from 'redux-saga/effects'
import store from 'src/store'
import { createSagas } from 'src/helpers/redux'
import { IResponse } from 'src/services/storage/project/http'
import * as state from './state'
import { actions, Actions, PREFIX } from './actions'
import { setters } from './index'
import { selectors } from './selectors'
import { selectors as accountSelectors } from 'src/store/modules/account/selectors'

import { nanoid } from 'nanoid'
import * as agentsService from 'src/services/agents'

import {
  CRMAgent,
  mastraClientAuth,
  mastraClentCancel,
} from 'src/services/agents/client'
import { tools } from 'src/services/agents/tools'
import * as app from 'src/store/modules/app'

const toastId = 'assistant/message'

const sagas = createSagas<Actions>(actions, {
  openWindow: function* ({ payload }) {
    yield put(setters.setWindow({ open: true }))
    const isStreaming: boolean = yield select(selectors.isStreaming)
    if (isStreaming) {
      yield put(
        app.actions.toastDismiss.pending({
          id: toastId,
        })
      )
    }
  },
  closeWidow: function* ({ payload }) {
    yield put(setters.setWindow({ open: false }))
  },
  toogleWindow: function* ({ payload }) {
    const isWindowOpen: boolean = yield select(selectors.windowOpen)
    const isStreaming: boolean = yield select(selectors.isStreaming)

    yield put(setters.setWindow({ open: !isWindowOpen }))

    if (!isWindowOpen && isStreaming) {
      yield put(
        app.actions.toastDismiss.pending({
          id: toastId,
        })
      )
    }
    if (isWindowOpen && isStreaming) {
      yield put(
        app.actions.tost.pending({
          id: toastId,
          type: 'progress',
          icon: 'mingcute:ai-fill',
          title: 'Assistant',
          description: 'Writes a message...',
        })
      )
    }
  },
  updateWindow: function* ({ payload }) {
    yield put(setters.setWindow(payload))
  },
  sendMessage: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)

    yield put(
      actions.streamMessage.pending({
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
        threadId: payload.threadId,
        role: 'user',
        content: payload.text,
      })
    )
  },

  getMemoryThreadsPaginated: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const response: IResponse<{ threads: state.IChatThread[] }> = yield call(
      agentsService.getMemoryThreadsPaginated,
      {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      }
    )

    yield put(setters.setThreads(response.data.threads))

    const [defaultThread] = response.data.threads

    if (!defaultThread) {
      const newDefaultThread: IResponse<state.IChatThread> = yield call(
        agentsService.postMemoryThreads,
        {
          resourceId: accountId.toString(),
          agentId: agentsService.CRM_AGENT_ID,
          title: 'Default',
        }
      )

      yield put(setters.setThreadsNewItem(newDefaultThread.data))
    }
  },
  postMemoryThreads: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const thread: IResponse<agentsService.IPostMemoryThreadsData> = yield call(
      agentsService.postMemoryThreads,
      {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      }
    )

    // yield put(setters.setThreadsNewItem(thread.data))
  },
  patchMemoryThreadsItem: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const thread: IResponse<agentsService.IPatchMemoryThreadsItemData> =
      yield call(agentsService.patchMemoryThreadsItem, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })

    // yield put(setters.setThreadsUpdate(thread.data))
  },
  deleteMemoryThreadsItem: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)

    const thread: IResponse<agentsService.IDeleteMemoryThreadsItemData> =
      yield call(agentsService.deleteMemoryThreadsItem, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })

    // yield put(setters.setThreadsRemove(payload))
  },
  getMemoryThreadsItemMessages: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const threadMessages: IResponse<agentsService.IGetMemoryThreadsItemMessagesData> =
      yield call(agentsService.getMemoryThreadsItemMessages, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })

    const formattedMessages: state.IChatMessage[] =
      threadMessages.data.messages.map((msg: any) => {
        let content: string

        if (typeof msg.content === 'string') {
          content = msg.content
        } else if (msg.content && typeof msg.content === 'object') {
          if ('parts' in msg.content && Array.isArray(msg.content.parts)) {
            content = msg.content.parts
              .filter((part: any) => {
                if (typeof part === 'string') return true
                if (part.type === 'text') return true

                return false
              })
              .map((part: any) => {
                if (typeof part === 'string') return part
                if (part.text) return part.text

                return ''
              })
              .filter((text: string) => text.trim().length > 0)
              .join('\n')
          } else if (
            'content' in msg.content &&
            typeof msg.content.content === 'string'
          ) {
            content = msg.content.content
          } else {
            content = msg.content.text || msg.content.content || ''
          }
        } else {
          content = String(msg.content || '')
        }

        if (!content.trim() && msg.role === 'assistant') {
          content = '(Agent processed your request)'
        }

        return {
          id: msg.id,
          role: msg.role as 'user' | 'assistant',
          content,
          createdAt: msg.createdAt,
        }
      })

    yield put(
      setters.setThreadsItemMessages({
        threadId: payload.threadId,
        messages: formattedMessages,
      })
    )
  },

  logging: {
    effect: (pattern, saga) => takeLatest('*', saga),
    controlled: true,
    saga: function* (data) {
      if (data.type.includes(PREFIX) && !data.type.includes('cacheStorage')) {
        yield put(actions.cacheStorage.pending())
      }
    },
  },
  cacheStorage: {
    effect: 'latest',
    controlled: true,
    saga: function* (data) {
      if (data.type.includes(PREFIX)) {
        yield delay(1000)
        try {
          const stateData: state.IStateData = yield select(selectors.data)
          localStorage.setItem(PREFIX, JSON.stringify(stateData))
        } catch (e) {
          console.debug(e)
        }
      }
    },
  },
  streamMessageCancel: function* ({ payload }) {
    yield put(setters.setStreamMessagesCancel(payload))
    try {
      mastraClentCancel()
    } catch (e) {}
  },
  streamMessage: function* ({ payload }) {
    try {
      mastraClientAuth()
      yield put(
        setters.setThreadsItemMessagesAppend({
          threadId: payload.threadId,
          message: {
            id: nanoid(),
            role: 'user',
            content: payload.content,
            createdAt: new Date().toISOString(),
          },
        })
      )

      const assistantMessageId = `assistant-${Date.now()}`
      let content = ''
      let assistantMessage = {
        id: assistantMessageId,
        role: 'assistant' as const,
        content,
        createdAt: new Date().toISOString(),
      }

      yield put(
        setters.setThreadsItemMessagesAppend({
          threadId: payload.threadId,
          message: {
            ...assistantMessage,
            streamingText: 'Thinking...',
          },
        })
      )

      const streamOptions = {
        clientTools: tools as any,
        memory: {
          thread: payload.threadId,
          resource: payload.resourceId,
        },
      }

      CRMAgent.stream(payload.content, streamOptions).then((stream) => {
        stream.processDataStream({
          onChunk: async (chunk) => {
            if (chunk.type === 'text-delta') {
              content += chunk.payload.text
              assistantMessage = { ...assistantMessage, content }
              store.dispatch(
                setters.setThreadsItemMessagesUpdate({
                  threadId: payload.threadId,
                  message: assistantMessage,
                })
              )
              if (content.trim()) {
                store.dispatch(
                  setters.setThreadsItemMessagesUpdate({
                    threadId: payload.threadId,
                    message: {
                      ...assistantMessage,
                      streamingText: undefined,
                    },
                  })
                )
              }
            } else if (chunk.type === 'text-start') {
              store.dispatch(
                setters.setThreadsItemMessagesUpdate({
                  threadId: payload.threadId,
                  message: {
                    ...assistantMessage,
                    streamingText: 'Writing...',
                  },
                })
              )
            } else if (chunk.type === 'text-end') {
              store.dispatch(
                setters.setThreadsItemMessagesUpdate({
                  threadId: payload.threadId,
                  message: {
                    ...assistantMessage,
                    streamingText: undefined,
                  },
                })
              )
            } else if (chunk.type === 'step-start') {
              store.dispatch(
                setters.setThreadsItemMessagesUpdate({
                  threadId: payload.threadId,
                  message: {
                    ...assistantMessage,
                    streamingText: 'Thinking...',
                  },
                })
              )
            } else if (chunk.type === 'tool-call-input-streaming-start') {
              const toolName = chunk.payload?.toolName ?? 'tool'

              store.dispatch(
                setters.setThreadsItemMessagesUpdate({
                  threadId: payload.threadId,
                  message: {
                    ...assistantMessage,
                    streamingText: `Calling tool ${toolName}...`,
                  },
                })
              )
            } else if (chunk.type === 'tool-call') {
              const toolName = chunk.payload?.toolName ?? 'tool'
              store.dispatch(
                setters.setThreadsItemMessagesUpdate({
                  threadId: payload.threadId,
                  message: {
                    ...assistantMessage,
                    streamingText: `Calling tool ${toolName}...`,
                  },
                })
              )
            } else if (chunk.type === 'tool-result') {
              const toolName = chunk.payload?.toolName ?? 'tool'

              store.dispatch(
                setters.setThreadsItemMessagesUpdate({
                  threadId: payload.threadId,
                  message: {
                    ...assistantMessage,
                    streamingText: `Tool ${toolName} finished`,
                  },
                })
              )
            } else if (chunk.type === 'tool-output') {
              const out = chunk.payload?.output
              if (out && typeof out === 'object' && out.type) {
                if (out.type === 'start') {
                  const toolName = chunk.payload?.toolName ?? 'sub-agent'
                  store.dispatch(
                    setters.setThreadsItemMessagesUpdate({
                      threadId: payload.threadId,
                      message: {
                        ...assistantMessage,
                        streamingText: `Running ${toolName}...`,
                      },
                    })
                  )
                } else if (out.type === 'step-start') {
                  store.dispatch(
                    setters.setThreadsItemMessagesUpdate({
                      threadId: payload.threadId,
                      message: {
                        ...assistantMessage,
                        streamingText: 'Thinking...',
                      },
                    })
                  )
                } else if (out.type === 'text-start') {
                  store.dispatch(
                    setters.setThreadsItemMessagesUpdate({
                      threadId: payload.threadId,
                      message: {
                        ...assistantMessage,
                        streamingText: 'Writing...',
                      },
                    })
                  )
                } else if (out.type === 'text-delta' && out.payload?.text) {
                  content += out.payload.text
                  assistantMessage = { ...assistantMessage, content }
                  store.dispatch(
                    setters.setThreadsItemMessagesUpdate({
                      threadId: payload.threadId,
                      message: assistantMessage,
                    })
                  )
                  if (content.trim()) {
                    store.dispatch(
                      setters.setThreadsItemMessagesUpdate({
                        threadId: payload.threadId,
                        message: {
                          ...assistantMessage,
                          streamingText: undefined,
                        },
                      })
                    )
                  }
                } else if (out.type === 'text-end') {
                  store.dispatch(
                    setters.setThreadsItemMessagesUpdate({
                      threadId: payload.threadId,
                      message: {
                        ...assistantMessage,
                        streamingText: undefined,
                      },
                    })
                  )
                } else if (out.type === 'tool-call') {
                  const toolName =
                    out.payload?.toolName ??
                    out.payload?.name ??
                    chunk.payload?.toolName ??
                    'tool'
                  store.dispatch(
                    setters.setThreadsItemMessagesUpdate({
                      threadId: payload.threadId,
                      message: {
                        ...assistantMessage,
                        streamingText: `Calling tool ${toolName}...`,
                      },
                    })
                  )
                } else if (out.type === 'tool-result') {
                  const toolName =
                    out.payload?.toolName ??
                    out.payload?.name ??
                    chunk.payload?.toolName ??
                    'tool'
                  store.dispatch(
                    setters.setThreadsItemMessagesUpdate({
                      threadId: payload.threadId,
                      message: {
                        ...assistantMessage,
                        streamingText: `Tool ${toolName} finished`,
                      },
                    })
                  )
                } else if (
                  out.type === 'step-finish' ||
                  out.type === 'finish'
                ) {
                  store.dispatch(
                    setters.setThreadsItemMessagesUpdate({
                      threadId: payload.threadId,
                      message: {
                        ...assistantMessage,
                        streamingText: undefined,
                      },
                    })
                  )
                }
              }
            } else if (chunk.type === 'error') {
              console.error('❌ Stream error:', chunk.payload.error)
              store.dispatch(
                setters.setThreadsItemMessagesUpdate({
                  threadId: payload.threadId,
                  message: {
                    ...assistantMessage,
                    streamingText: undefined,
                    content: content || `Stream error: ${chunk.payload.error}`,
                  },
                })
              )
            } else if (chunk.type === 'finish') {
              store.dispatch(
                setters.setThreadsItemMessagesUpdate({
                  threadId: payload.threadId,
                  message: {
                    ...assistantMessage,
                    streamingText: undefined,
                  },
                })
              )
            }
          },
        })
      })
    } catch (e) {
      console.debug(e)
    }
  },
})

export default sagas
